import { Path } from "app/path";
import {
  blacklistFromAccount,
  selectAccount,
  unblackListFromAccount,
} from "app/redux/accountSlice";
import { getClients, selectClients } from "app/redux/clientsSlice";
import { selectActiveEmployees } from "app/redux/employeesSlice";
import { useQuery } from "app/utils/useQuery";
import { includes } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Papa from "papaparse";
import http from "app/requests/axiosInstance";

export const perPageList = [10, 20, 50];
export const sortByList = [
  { title: "Recent", value: "updatedAt" },
  { title: "Name", value: "name" },
  { title: "Email", value: "email" },
];

export const useClients = () => {
  const {
    clients,
    meta: { totalCount },
  } = useSelector(selectClients);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const account = useSelector(selectAccount);
  const employees = useSelector(selectActiveEmployees);
  const pageParam = query.get("page");
  const clientIdParam = query.get("clientId");
  const searchParam = query.get("search") || "";
  const managerIdsToFilterParam = query.get("managerIdsToFilter");
  const [rowsPerPage, setRowsPerPage] = useState(perPageList[0]);
  const [sortBy, setSortBy] = useState(sortByList[0]);
  const [shouldShowClientDrawer, setShouldShowClientDrawer] = useState(false);
  const [shouldShowBlacklistDrawer, setShouldShowBlacklistDrawer] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchParam);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [managerIdsToFilter, setManagerIdsToFilter] = useState<number[]>(
    managerIdsToFilterParam?.split(",").map(Number) || []
  );

  const fetchClients = async (payload?: any) => {
    setLoading(true);
    try {
      await dispatch(
        getClients({
          perPage: rowsPerPage,
          sortBy: sortBy.value,
          page: Number(pageParam) || 1,
          sortOrder: sortBy.value === "createdAt" ? "asc" : "desc",
          managerIds: managerIdsToFilter,
          search: searchTerm,
          ...payload,
        }) as any
      ).unwrap();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search — updates URL which triggers main useEffect
  const onSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(async () => {
      if (term.trim()) {
        query.set("search", term.trim());
      } else {
        query.delete("search");
      }
      query.set("page", "1");
      navigate(`${Path.CLIENTS}?${query.toString()}`);
    }, 350);
  }, [rowsPerPage, sortBy, managerIdsToFilter]);

  const onClientRowClick = (id?: number) => {
    setShouldShowClientDrawer(true);
    query.delete("clientId");
    query.set("clientId", id!.toString());
    navigate(`${Path.CLIENTS}?${query.toString()}`);
  };

  const onOpenNewClientDrawer = () => {
    query.delete("clientId");
    navigate(`${Path.CLIENTS}?${query.toString()}`);
    if (!shouldShowClientDrawer) {
      setShouldShowClientDrawer(true);
    }
  };

  const onBlacklistAdd = async (
    identifier: string,
    identifierType?: string
  ) => {
    try {
      await dispatch(
        blacklistFromAccount({
          identifier: identifier.toLowerCase(),
          identifierType: identifierType || "email",
        }) as any
      ).unwrap();
      toast.success(`${identifier} blacklisted successfully`);
    } catch (error: any) {
      if (
        includes(error?.data?.details?.identifier, "has already been taken")
      ) {
        toast.error(`${identifier} is already blacklisted`);
        return;
      }
      console.error(error);
    }
  };

  const onBlacklistRemove = async (
    identifier: string,
    identifierType?: string
  ) => {
    try {
      await dispatch(
        unblackListFromAccount({
          identifier,
          identifierType: "email",
        }) as any
      ).unwrap();
      toast.success(`${identifier} has been unblacklisted successfully`);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchClients();
    })();
  }, [rowsPerPage, sortBy, pageParam, managerIdsToFilter, searchParam]);

  useEffect(() => {
    (async () => {
      const areManagerIdsToFilter = managerIdsToFilter.length > 0;

      if (areManagerIdsToFilter) {
        query.set("managerIdsToFilter", managerIdsToFilter.join(","));
      } else {
        query.delete("managerIdsToFilter");
      }
      navigate(`${Path.CLIENTS}?${query.toString()}`);

      if (managerIdsToFilterParam || areManagerIdsToFilter) {
        query.set("page", "1");
        await fetchClients({ page: 1 });
      }
    })();
  }, [managerIdsToFilter]);

  useEffect(() => {
    if (!shouldShowClientDrawer) {
      query.delete("clientId");
      navigate(`${Path.CLIENTS}?${query.toString()}`);
      setShouldShowClientDrawer(false);
    }
  }, [shouldShowClientDrawer]);

  const exportClients = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/v1/clients/export");
      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Clients exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export clients");
    } finally {
      setLoading(false);
    }
  };

  return {
    clients,
    rowsPerPage,
    setRowsPerPage,
    perPageList,
    sortBy,
    setSortBy,
    shouldShowClientDrawer,
    setShouldShowClientDrawer,
    onClientRowClick,
    clientIdParam,
    onOpenNewClientDrawer,
    loading,
    isSearching,
    totalCount,
    shouldShowBlacklistDrawer,
    setShouldShowBlacklistDrawer,
    onBlacklistAdd,
    onBlacklistRemove,
    account,
    navigate,
    employees,
    managerIdsToFilter,
    setManagerIdsToFilter,
    fetchClients,
    searchTerm,
    onSearchChange,
    exportClients,
  };
};
